import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Events } from '@df/core/service/event';
import { ImageModule } from '@df/module-image/image.module';
import { findMediaByLabel } from '@df/ui/common/find-media-by-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { TooltipComponent } from 'projects/app-default/src/app/ui/common/tooltip/tooltip.component';
import { NBuilderConfig } from '../../builder.config';
import { NBuilderProductsSetup } from '../builder-products-setup.config';
import { BuilderZoomDirective } from './zoom/builder-zoom.directive';

export interface IFocusData {
    posLeft: number;
    posTop: number;
    scale: number;
}

export interface IOptionImageData {
    image: string;
    shift: number;
    order: IOrderImage[];
    altImage?: string;
}

export interface IOrderImage {
    id: number;
    useAdditionalLabel: boolean;
    hideForAttrOption?: number;
}

@Component({
    selector: 'builder-gallery',
    templateUrl: './builder-gallery.html',
    styleUrls: ['./builder-gallery.scss'],
    standalone: true,
    imports: [SizeClassDirective, DfUiModule, TooltipComponent, TranslationModule, BuilderZoomDirective, ImageModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuilderGalleryComponent extends UiComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true })
    storageData!: NBuilderProductsSetup.IStorageData;
    @Input()
    horseImageData?: IOptionImageData;
    @Input()
    riderImageData?: IOptionImageData;
    @Input()
    background?: string;
    @Input({ required: true })
    currentOpenedOption!: NBuilderProductsSetup.IOptionElementData;
    @Input()
    basePosition?: IFocusData;
    @Input()
    displayAltImage?: IFocusData;

    protected zoomScale = 1;
    protected selectionHorseImages?: string[] = [];
    protected selectionRiderImages?: string[] = [];
    protected focusShiftTop?: number;
    protected focusShiftLeft?: number;
    protected isFocusActive = false;
    private focusData?: IFocusData;

    constructor(
        private productResource: ProductResource,
        private events: Events
    ) {
        super();
    }

    override ngOnInit(): void {
        this.subscriptions.add(
            this.events.on(NBuilderConfig.updateStorageEvent).subscribe(() => {
                this.updateImage();
                this.changeDetectorRef.detectChanges();
            })
        );

        this.updateImage();
    }

    override ngOnChanges(changes: any): void {
        if (changes['currentOpenedOption']) {
            this.focusData = this.bothImages
                ? ({
                      posLeft: this.currentOpenedOption?.focusLeftPosition,
                      posTop: this.currentOpenedOption?.focusTopPosition,
                      scale: this.currentOpenedOption?.focusScale
                  } as IFocusData)
                : ({
                      posLeft: this.currentOpenedOption?.focusLeftPositionForSingle,
                      posTop: this.currentOpenedOption?.focusTopPositionForSingle,
                      scale: this.currentOpenedOption?.focusScaleForSingle
                  } as IFocusData);
        }
        if (changes['currentOpenedOption'] && this.isFocusActive) {
            this.resetFocus();
            this.zoomScale = 1;
        }
    }

    private updateImage(): void {
        // undefined for horse to make it work for pony as well
        this.selectionHorseImages = this.horseImageData?.image
            ? this.getImagesBySelection('Horse', this.horseImageData?.order, true)
            : undefined;
        this.selectionRiderImages = this.riderImageData?.image ? this.getImagesBySelection('Rider', this.riderImageData?.order) : undefined;
    }

    private getImagesBySelection(productType: string, order: IOrderImage[], canBeUndefined = false): string[] {
        return order
            .map(e => {
                const selection = this.storageData.selection?.[e.id];
                const productId =
                    selection && (selection.productType === productType || (canBeUndefined && !selection.productType))
                        ? selection.matchedProductId || selection.productId
                        : undefined;
                const product = productId && this.productResource.getExisting(productId);
                return (
                    product &&
                    (!e.hideForAttrOption || !this.storageData.selection?.[e.hideForAttrOption]) &&
                    ((e.useAdditionalLabel && findMediaByLabel(product.familyMedia || [], 'outfit_builder_additional')) ||
                        findMediaByLabel(product.familyMedia || [], 'outfit_builder_image'))
                );
            })
            .filter(e => typeof e === 'string') as string[];
    }

    protected zoomIn(): void {
        if (this.zoomScale < 3) {
            this.zoomScale = this.zoomScale + 0.25;
            this.changeDetectorRef.detectChanges();
        }
    }

    protected zoomOut(): void {
        if (this.zoomScale > 1) {
            this.zoomScale = this.zoomScale - 0.25;
            this.changeDetectorRef.detectChanges();
        }
    }

    protected focus(): void {
        if (this.isFocusActive) {
            this.focusShiftLeft = undefined;
            this.focusShiftTop = undefined;
            this.zoomScale = 1;
            this.isFocusActive = false;
        } else {
            this.focusShiftLeft = this.focusData?.posLeft;
            this.focusShiftTop = this.focusData?.posTop;
            this.zoomScale = this.focusData?.scale || 2;
            this.isFocusActive = true;
        }
        this.changeDetectorRef.detectChanges();
    }

    protected resetFocus(): void {
        this.focusShiftLeft = undefined;
        this.focusShiftTop = undefined;
        this.isFocusActive = false;
        this.changeDetectorRef.detectChanges();
    }

    get isZoomActive() {
        return this.zoomScale !== 1;
    }

    get canFocus(): boolean {
        return !!this.focusData && this.focusData.posLeft !== undefined && this.focusData.posTop !== undefined;
    }

    get bothImages(): boolean {
        return !!this.riderImageData?.image && !!this.horseImageData?.image;
    }
}
